import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { PlusIcon } from '../../../../components/icons';
import Input from '../../../../components/ui/Input';
import { useTagSelector } from './hooks/useTagSelector';
import TagFormModal from '../../../settings/tags/components/TagFormModal';
import ConfirmationModal from '../../../../components/shared/ConfirmationModal';

interface TagSelectorProps {
    leadId: string;
    selectedTags: string[];
    onTagsChange: (tagIds: string[]) => void;
}

export const TagSelector: React.FC<TagSelectorProps> = ({ leadId, selectedTags, onTagsChange }) => {
    const {
        isOpen,
        setIsOpen,
        searchTerm,
        setSearchTerm,
        isCreateModalOpen,
        setIsCreateModalOpen,
        dropdownRef,
        selectedTagObjects,
        filteredTags,
        handleAddTag,
        handleRemoveTag,
        handleCreateTag,
        handleUpdateTag,
        handleDeleteTagSystem,
        tagToEdit,
        openEditModal,
        openDeleteModal,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        tagToDelete
    } = useTagSelector({ selectedTags, onTagsChange });

    return (
        <>
            <div className="space-y-s2">
                {selectedTagObjects.length > 0 && (
                    <ul className="m-0 flex list-none flex-wrap gap-s2 p-0" aria-label="Tags selecionadas">
                        {selectedTagObjects.map((tag) => (
                            <li
                                key={tag.id}
                                className="group relative inline-flex items-center gap-s1 rounded-full px-s4 py-s1 transition-all hover:shadow-sm"
                                style={{ backgroundColor: tag.color }}
                                title={tag.name}
                            >
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveTag(tag.id);
                                    }}
                                    className="hover:bg-card/20 rounded-full p-0.5 transition-colors flex-shrink-0"
                                    title="Remover tag"
                                    aria-label={`Remover tag ${tag.name}`}
                                >
                                    <AppIcon name="x-circle" className="w-3 h-3 text-white" />
                                </button>

                                {/* Tooltip */}
                                <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-s2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-s2 py-s1 text-xs text-primary-foreground opacity-0 transition-opacity group-hover:opacity-100">
                                    {tag.name}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="relative" ref={dropdownRef}>
                    <button
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        className="flex min-h-11 touch-manipulation items-center gap-s2 text-base text-muted transition-colors hover:text-primary md:min-h-0"
                        aria-label="Adicionar tag"
                        aria-expanded={isOpen}
                    >
                        Adicionar tag
                    </button>

                    {isOpen && (
                        <div className="absolute left-0 z-50 mt-s2 w-64 rounded-lg border border-subtle bg-surface-0 shadow-lg">
                            <div className="border-b border-subtle p-s3">
                                <Input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Buscar tags..."
                                    inputSize="sm"
                                    aria-label="Buscar tags"
                                    autoFocus
                                />
                            </div>

                            <div className="max-h-60 overflow-y-auto p-s2">
                                {filteredTags.length === 0 ? (
                                    <p className="py-s4 text-center text-sm text-muted">
                                        {searchTerm
                                            ? 'Nenhuma tag encontrada'
                                            : 'Todas as tags já foram adicionadas'}
                                    </p>
                                ) : (
                                    filteredTags.map((tag) => (
                                        <div
                                            key={tag.id}
                                            role="button"
                                            tabIndex={0}
                                            className="group flex w-full cursor-pointer items-center justify-between rounded-lg px-s3 py-s2 transition-colors hover:bg-surface-subtle"
                                            onClick={() => handleAddTag(tag.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleAddTag(tag.id);
                                                }
                                            }}
                                        >
                                            <div className="flex min-w-0 items-center gap-s2 overflow-hidden">
                                                <span
                                                    className="size-4 shrink-0 rounded-full"
                                                    style={{ backgroundColor: tag.color }}
                                                    aria-hidden
                                                />
                                                <span className="truncate text-sm text-default">
                                                    {tag.name}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-s1 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditModal(tag);
                                                    }}
                                                    className="rounded-md p-s1 text-muted transition-colors hover:bg-surface-0 hover:text-primary"
                                                    title="Editar"
                                                    aria-label={`Editar tag ${tag.name}`}
                                                >
                                                    <AppIcon name="edit" className="size-3.5" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDeleteModal(tag);
                                                    }}
                                                    className="rounded-md p-s1 text-muted transition-colors hover:bg-surface-0 hover:text-danger-text"
                                                    title="Excluir"
                                                    aria-label={`Excluir tag ${tag.name}`}
                                                >
                                                    <AppIcon name="trash" className="size-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div className="border-t border-subtle p-s2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsOpen(false);
                                        setIsCreateModalOpen(true);
                                    }}
                                    className="flex w-full items-center gap-s2 rounded-lg px-s3 py-s2 text-sm text-primary transition-colors hover:bg-primary-subtle"
                                    aria-label="Criar nova tag"
                                >
                                    <AppIcon name="plus" className="size-4" aria-hidden />
                                    Criar nova tag
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de criar/editar tag */}
            <TagFormModal
                isOpen={isCreateModalOpen}
                onClose={() => {
                    setIsCreateModalOpen(false);
                    // Reset controlado por hook, mas garantimos aqui também se precisasse
                }}
                onSave={tagToEdit ? handleUpdateTag : handleCreateTag}
                tagToEdit={tagToEdit}
            />

            {/* Modal de confirmação de exclusão */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteTagSystem}
                title="Excluir Etiqueta"
                message={`Tem certeza que deseja excluir a etiqueta "${tagToDelete?.name}"? Isso a removerá de todos os leads/negociações.`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />
        </>
    );
};
